import { type ReactNode } from "react";
import { match } from "ts-pattern";
import { classNames } from "../../utils/class-names";

interface BaseBreakdownProps {
  className?: string;
  items: ReactNode[];
}

export type Align = "left" | "right";

interface BreakdownProps extends BaseBreakdownProps {
  align: Align;
}

export function Breakdown({ align, ...props }: BreakdownProps): JSX.Element {
  return match(align)
    .with("left", () => <LeftAlignedBreakdown {...props} />)
    .with("right", () => <RightAlignedBreakdown {...props} />)
    .exhaustive();
}

function LeftAlignedBreakdown({
  className,
  items,
}: BaseBreakdownProps): JSX.Element {
  return (
    <div
      className={classNames("grid", className)}
      style={{
        gridTemplateColumns: `0px 16px 10px auto`,
      }}
    >
      <div
        className="border-l border-dashed border-shale-400"
        style={{
          gridRowStart: 1,
          gridRowEnd: items.length * 2,
        }}
      />

      {items.map((item) => (
        <>
          <div className="border-b border-dashed border-shale-400" />
          <div />
          <div
            className="text-shale-200"
            style={{
              gridRowStart: "span 2",
            }}
          >
            {item}
          </div>
          <div />
          <div />
        </>
      ))}
    </div>
  );
}

function RightAlignedBreakdown({
  className,
  items,
}: BaseBreakdownProps): JSX.Element {
  return (
    <div
      className={classNames("grid", className)}
      style={{
        gridTemplateColumns: `auto 10px 16px 0px`,
      }}
    >
      {items.map((item, i) => (
        <>
          <div
            className="text-right text-shale-200 justify-self-end"
            style={{
              gridRowStart: "span 2",
            }}
          >
            {item}
          </div>
          <div />
          <div className="border-b border-dashed border-shale-400" />

          {i === 0 ? (
            <div
              className="border-l border-dashed border-shale-400"
              style={{
                gridRowStart: 1,
                gridRowEnd: items.length * 2,
                gridColumnStart: 4,
              }}
            />
          ) : null}

          <div />
          <div />
        </>
      ))}
    </div>
  );
}
